import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { ArrowLeft, CheckCircle2, XCircle, HelpCircle, Clock, BookOpen } from "lucide-react";
import { toast } from "sonner";

const DIFF_LABEL = { 1: "Foundational", 2: "Intermediate", 3: "Advanced", 4: "Expert", 5: "Exceptional" };

export default function Review() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("cognitive");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/assessment/${aid}/review`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) { nav("/auth"); return; }
      toast.error(e?.response?.data?.detail || "Could not load review");
    } finally { setLoading(false); }
  }, [aid, nav]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div><Nav /><div className="max-w-3xl mx-auto px-5 py-20 text-slate-400">Loading your answers…</div></div>;
  if (!data) return null;

  const list = tab === "cognitive" ? data.cognitive.items : data.emotional.items;
  const cogAcc = data.cognitive.accuracy;

  return (
    <div><Nav />
      <div className="max-w-3xl mx-auto px-5 py-8">
        <button onClick={() => nav(`/results/${aid}`)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 mb-4" data-testid="back-to-results">
          <ArrowLeft className="w-4 h-4" /> Back to results
        </button>

        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-2">Your answers</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-3" data-testid="review-title">Question-by-question review</h1>
        <p className="text-slate-400 text-sm max-w-xl mb-6">
          Here is every item you answered, with the correct choice and a short explanation.
          Emotional items use multi-dimensional scoring, so no single option is marked "correct."
        </p>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Cognitive scored" value={`${data.cognitive.correct}/${data.cognitive.scored_total}`} />
          <StatCard label="Accuracy" value={cogAcc != null ? `${Math.round(cogAcc * 100)}%` : "—"} />
          <StatCard label="Emotional items" value={`${data.emotional.scored_total}`} />
          <StatCard label="Total items" value={`${data.cognitive.items.length + data.emotional.items.length}`} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-5">
          <button onClick={() => setTab("cognitive")} data-testid="tab-cognitive"
            className={`px-4 py-2 rounded-lg text-sm ${tab==="cognitive" ? "bg-[#38BDF8] text-[#070C18] font-semibold" : "border border-slate-700 text-slate-300 hover:border-slate-500"}`}>
            Cognitive · {data.cognitive.items.length}
          </button>
          <button onClick={() => setTab("emotional")} data-testid="tab-emotional"
            className={`px-4 py-2 rounded-lg text-sm ${tab==="emotional" ? "bg-[#F59E0B] text-[#070C18] font-semibold" : "border border-slate-700 text-slate-300 hover:border-slate-500"}`}>
            Emotional · {data.emotional.items.length}
          </button>
        </div>

        <div className="space-y-4" data-testid="review-list">
          {list.length === 0 && <div className="text-slate-500 text-sm">No items in this section.</div>}
          {list.map(it => (
            <ReviewItem key={it.question_id} it={it} />
          ))}
        </div>

        <div className="mt-10 text-xs text-slate-500 border-t border-slate-800 pt-4 flex items-center gap-2">
          <BookOpen className="w-3.5 h-3.5" />
          Reviewing wrong answers once is one of the strongest learning signals we track.
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl bg-[#131E36] border border-slate-800/70 p-4">
      <div className="text-[11px] uppercase font-mono tracking-widest text-slate-500">{label}</div>
      <div className="font-serif text-2xl mt-1 text-slate-100">{value}</div>
    </div>
  );
}

function ReviewItem({ it }) {
  const isCorrect = it.correctness === true;
  const isWrong = it.correctness === false;
  const isUnscored = it.correctness === null || it.correctness === undefined;
  const borderCls = isCorrect ? "border-emerald-500/40" : isWrong ? "border-rose-500/40" : "border-slate-800/70";
  const Icon = isCorrect ? CheckCircle2 : isWrong ? XCircle : HelpCircle;
  const iconCls = isCorrect ? "text-emerald-400" : isWrong ? "text-rose-400" : "text-slate-500";

  return (
    <div className={`rounded-2xl bg-[#131E36] border ${borderCls} p-5`} data-testid={`review-item-${it.question_id}`}>
      <div className="flex items-center gap-2 text-xs font-mono text-slate-500 mb-3 flex-wrap">
        <span className="text-[#E0A96D]">#{it.position}</span>
        <span>·</span>
        <span className="capitalize">{(it.domain || "").replace(/_/g, " ")}</span>
        {it.difficulty && <><span>·</span><span>{DIFF_LABEL[it.difficulty] || it.difficulty}</span></>}
        {it.response_time_ms != null && (
          <><span>·</span><span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{(it.response_time_ms/1000).toFixed(1)}s</span></>
        )}
        <span className={`ml-auto inline-flex items-center gap-1 ${iconCls}`}>
          <Icon className="w-4 h-4" />
          {isCorrect ? "Correct" : isWrong ? "Not quite" : "Reflective item"}
        </span>
      </div>

      <div className="font-serif text-lg text-slate-100 mb-4">{it.prompt}</div>

      <ol className="space-y-2 text-sm" data-testid={`options-${it.question_id}`}>
        {it.options.map((opt, i) => {
          const isUserChoice = i === it.selected_index;
          const isCorrectChoice = it.correct_index != null && i === it.correct_index;
          let cls = "border-slate-800 text-slate-300";
          let tag = null;
          if (isCorrectChoice && isUserChoice) {
            cls = "border-emerald-500/60 bg-emerald-500/10 text-emerald-100";
            tag = <span className="text-xs text-emerald-300">✓ your answer · correct</span>;
          } else if (isCorrectChoice) {
            cls = "border-emerald-500/40 bg-emerald-500/5 text-emerald-200";
            tag = <span className="text-xs text-emerald-300">✓ correct answer</span>;
          } else if (isUserChoice && isWrong) {
            cls = "border-rose-500/50 bg-rose-500/10 text-rose-100";
            tag = <span className="text-xs text-rose-300">✗ your answer</span>;
          } else if (isUserChoice) {
            cls = "border-slate-600 bg-slate-800/40 text-slate-100";
            tag = <span className="text-xs text-slate-300">your answer</span>;
          }
          return (
            <li key={`${it.question_id}-opt-${i}`} className={`rounded-lg border ${cls} px-3 py-2 flex items-start gap-3`}>
              <span className="font-mono text-xs text-slate-500 w-5 mt-0.5">{String.fromCharCode(65 + i)}.</span>
              <span className="flex-1">{opt}</span>
              {tag}
            </li>
          );
        })}
      </ol>

      {it.explanation && (
        <div className="mt-4 rounded-lg bg-[#0E172A] border border-slate-800 p-3">
          <div className="text-[11px] uppercase font-mono tracking-widest text-slate-500 mb-1">Why</div>
          <div className="text-sm text-slate-300">{it.explanation}</div>
        </div>
      )}
      {!it.explanation && isUnscored && (
        <div className="mt-4 text-xs text-slate-500 italic">
          This item measures a trait pattern rather than a right/wrong answer, so there is no single explanation to show.
        </div>
      )}
    </div>
  );
}
